import { getGuidsToSkip } from '@thoughtspot/session-service';
import { useEffect, useRef, useState } from 'react';
import { SortOptionOrder, Users__AccountStatus, } from "../../../../../services/generated/graphql-types";
import { useSearchUsersV2Query, } from "../../../../../services/user-group/user-group-service";
import { getSelectedOnCurrentPage, onRowsSelectAction, refreshCells, updateRowData, } from '../../../../user-management/util';
import { useAdminUserTracking, useListLoading } from '../../shared';
import { getDefaultUserListParams, shouldShowPendingForInactive, SORT_KEY_MAP, TableColumnId, } from '../constants';
const parseUsersData = (objects) => {
    const remapInactiveToPending = shouldShowPendingForInactive();
    return objects.map(user => {
        const groups = (user.user_groups ?? [])
            .filter((g) => g !== null)
            .map(g => ({ id: g.id ?? '', name: g.name ?? '' }))
            .filter(g => g.id);
        return {
            id: user.id,
            name: user.name || '',
            displayName: user.display_name || '',
            email: user.email || '',
            type: user.account_type || '',
            orgIds: (user.orgs ?? [])
                .filter((o) => o !== null)
                .map(o => o.id),
            groups,
            groupNames: groups.map(g => g.name),
            created: user.creation_time_in_millis ?? 0,
            userState: remapInactiveToPending &&
                user.account_status === Users__AccountStatus.Inactive
                ? Users__AccountStatus.Pending
                : user.account_status || '',
            isChecked: false,
        };
    });
};
export const useUsersList = ({ isClusterAdmin, currentOrgId, orgNameMap, orgsListLoading, }) => {
    const tracking = useAdminUserTracking();
    const gridRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [selectedRowsIds, setSelectedRowsIds] = useState([]);
    const [lastOperatedPage, setLastOperatedPage] = useState(0);
    const orgScope = isClusterAdmin ? undefined : [String(currentOrgId)];
    const [userListInput, setUserListInput] = useState(() => ({
        ...getDefaultUserListParams(),
        org_identifiers: orgScope,
    }));
    const { data: usersData, loading: usersListLoading, error: usersListError, refetch: refetchUsers, } = useSearchUsersV2Query(userListInput);
    const selectedRowsIdsRef = useRef(selectedRowsIds);
    selectedRowsIdsRef.current = selectedRowsIds;
    const pendingActionRef = useRef(null);
    const orgsReady = !isClusterAdmin || !orgsListLoading;
    const { gridLoading, isLoading, setIsLoading } = useListLoading({
        queryLoading: usersListLoading,
        data: usersData,
        error: usersListError,
        ready: orgsReady,
    });
    useEffect(() => {
        if (usersData && !usersListLoading && orgsReady) {
            const guidsToSkip = new Set(getGuidsToSkip());
            const parsed = parseUsersData((usersData.data ?? []).filter((u) => u !== null && !guidsToSkip.has(u.id)));
            const checked = getSelectedOnCurrentPage(selectedRowsIdsRef.current, parsed);
            updateRowData(parsed, checked, setRowData);
            const pending = pendingActionRef.current;
            if (pending) {
                const resultCount = parsed.length;
                if (pending.kind === 'search') {
                    tracking.listSearched({
                        queryLength: pending.queryLength,
                        resultCount,
                    });
                }
                else if (pending.kind === 'filter') {
                    tracking.listFiltered({
                        filterField: pending.filterField,
                        filterValue: pending.filterValue,
                        resultCount,
                    });
                }
                else if (pending.kind === 'sort') {
                    tracking.listSorted({
                        sortField: pending.sortField,
                        sortDirection: pending.sortDirection,
                    });
                }
                else if (pending.kind === 'paginate') {
                    tracking.listPaginated({
                        pageNumber: pending.pageNumber,
                        pageSize: pending.pageSize,
                    });
                }
                pendingActionRef.current = null;
            }
        }
    }, [usersData, usersListLoading, orgsReady, tracking]);
    useEffect(() => {
        const filtered = getSelectedOnCurrentPage(selectedRowsIds, rowData);
        if (gridRef.current) {
            updateRowData(rowData, filtered, setRowData);
            refreshCells(gridRef, filtered, TableColumnId.ACTIONS);
        }
    }, [selectedRowsIds]);
    useEffect(() => {
        if (isClusterAdmin && gridRef.current && !orgsListLoading) {
            refreshCells(gridRef, [], TableColumnId.GROUPS_OR_ORGS);
        }
    }, [orgNameMap]);
    const refreshUsersList = () => {
        setIsLoading(true);
        return refetchUsers(userListInput).finally(() => setIsLoading(false));
    };
    const onChangeOffset = (offset) => {
        const pageSize = userListInput.record_size ?? 0;
        setUserListInput(prev => ({ ...prev, record_offset: offset }));
        setIsLoading(true);
        pendingActionRef.current = {
            kind: 'paginate',
            pageNumber: pageSize > 0 ? Math.floor(offset / pageSize) + 1 : 1,
            pageSize,
        };
    };
    const onSearchChange = (term) => {
        setUserListInput(prev => ({
            ...prev,
            record_offset: 0,
            name_pattern: term || undefined,
        }));
        setIsLoading(true);
        pendingActionRef.current = {
            kind: 'search',
            queryLength: term?.length ?? 0,
        };
    };
    const onGroupFilterChange = (groupIds) => {
        setUserListInput(prev => ({
            ...prev,
            record_offset: 0,
            group_identifiers: groupIds.length ? groupIds : undefined,
        }));
        setIsLoading(true);
        pendingActionRef.current = {
            kind: 'filter',
            filterField: 'group',
            filterValue: groupIds,
        };
    };
    const onOrgFilterChange = (orgIds) => {
        setUserListInput(prev => ({
            ...prev,
            record_offset: 0,
            org_identifiers: orgIds.length ? orgIds : orgScope,
        }));
        setIsLoading(true);
        pendingActionRef.current = {
            kind: 'filter',
            filterField: 'org',
            filterValue: orgIds,
        };
    };
    const onStatusFilterChange = (status) => {
        setUserListInput(prev => ({
            ...prev,
            record_offset: 0,
            account_status: status ?? undefined,
        }));
        setIsLoading(true);
        pendingActionRef.current = {
            kind: 'filter',
            filterField: 'status',
            filterValue: status,
        };
    };
    const onClearAllFilters = () => {
        setUserListInput(prev => ({
            ...prev,
            record_offset: 0,
            group_identifiers: undefined,
            org_identifiers: orgScope,
            account_status: undefined,
        }));
        setIsLoading(true);
    };
    const onSortingChanged = (sortState) => {
        const col = sortState.find(el => el.sort === 'asc' || el.sort === 'desc');
        const fieldName = col && SORT_KEY_MAP[col.colId];
        const order = col?.sort === 'asc' ? SortOptionOrder.Asc : SortOptionOrder.Desc;
        setUserListInput(prev => ({
            ...prev,
            record_offset: 0,
            sort_options: fieldName
                ? { field_name: fieldName, order }
                : undefined,
        }));
        setIsLoading(true);
        if (fieldName) {
            pendingActionRef.current = {
                kind: 'sort',
                sortField: fieldName,
                sortDirection: order === SortOptionOrder.Asc ? 'asc' : 'desc',
            };
        }
    };
    const onRowsSelected = (rowIds) => {
        onRowsSelectAction(rowIds, rowData, { offset: userListInput.record_offset ?? 0 }, lastOperatedPage, setLastOperatedPage, selectedRowsIds, setSelectedRowsIds);
    };
    const onGridReady = (api) => {
        gridRef.current = api;
    };
    const isLastBatch = gridLoading || (usersData?.isLastBatch ?? true);
    return {
        rowData,
        gridLoading,
        isLoading,
        setIsLoading,
        selectedRowsIds,
        setSelectedRowsIds,
        userListInput,
        isLastBatch,
        usersListError,
        refreshUsersList,
        onChangeOffset,
        onSearchChange,
        onSortingChanged,
        onRowsSelected,
        onGridReady,
        onGroupFilterChange,
        onOrgFilterChange,
        onStatusFilterChange,
        onClearAllFilters,
    };
};
//# sourceMappingURL=useUsersList.js.map